import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  TextField,
  Chip,
  Fab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListItemAvatar,
  Avatar,
  Paper,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ShoppingCart as ConvertIcon,
  KeyboardArrowUp as ArrowUpIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { wishlistService } from '../../services/toyService';
import { currencyService } from '../../services/currencyService';

function WishList() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  
  const [wishItems, setWishItems] = useState([]);
  const [filteredWishItems, setFilteredWishItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [error, setError] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openConvertDialog, setOpenConvertDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToConvert, setItemToConvert] = useState(null);
  const [converting, setConverting] = useState(false);

  const loadWishItems = useCallback(async () => {
    try {
      setLoading(true);
      const items = await wishlistService.getWishItems(currentUser.uid);
      setWishItems(items);
      setFilteredWishItems(items);
    } catch (error) {
      console.error('Error loading wish items:', error);
      setError('Failed to load wishlist items');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  const loadUserCurrency = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const currency = await currencyService.getUserCurrency(currentUser.uid);
      setUserCurrency(currency);
    } catch (error) {
      console.error('Error loading user currency:', error);
    }
  }, [currentUser?.uid]);

  // Load wishlist items
  useEffect(() => {
    if (currentUser?.uid) {
      loadWishItems();
      loadUserCurrency();
    }
  }, [currentUser?.uid, loadWishItems, loadUserCurrency]);

  // Filter items based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredWishItems(wishItems);
    } else {
      const filtered = wishItems.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.series?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWishItems(filtered);
    }
  }, [searchTerm, wishItems]);

  // Handle scroll for "back to top" button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function handleSearchChange(event) {
    setSearchTerm(event.target.value);
  }

  function handleViewModeChange(mode) {
    setViewMode(mode);
  }

  function handleScrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleEdit(item) {
    navigate(`/wish/${item.id}`);
  }

  function handleDelete(item) {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  }

  function handleConvert(item) {
    setItemToConvert(item);
    setOpenConvertDialog(true);
  }

  async function confirmDelete() {
    try {
      await wishlistService.deleteWishItem(itemToDelete.id);
      await loadWishItems();
      setOpenDeleteDialog(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting wish item:', error);
      setError('Failed to delete wish item');
    }
  }

  async function confirmConvert() {
    try {
      setConverting(true);
      await wishlistService.convertWishToToy(itemToConvert.id, currentUser.uid);
      await loadWishItems();
      setOpenConvertDialog(false);
      setItemToConvert(null);
      // Navigate to toys list to show the converted item
      navigate('/toys');
    } catch (error) {
      console.error('Error converting wish to toy:', error);
      setError('Failed to convert wish item to toy');
    } finally {
      setConverting(false);
    }
  }

  function getImageUrl(imageUrl, itemName) {
    if (imageUrl) {
      return imageUrl;
    }
    const encodedName = encodeURIComponent(itemName || 'Wish Item');
    return `https://placehold.co/400x300/e0e0e0/666666?text=${encodedName}`;
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 0 }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        py: { xs: 4, sm: 5, md: 6 },
        px: { xs: 2, sm: 3 },
        mb: 4,
        borderRadius: { xs: 0, sm: 2 },
        mx: { xs: -2, sm: 0 }
      }}>
        <Stack spacing={3}>
          {/* Title Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                mb: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              {t('wishlist.title')}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.9,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                fontWeight: 400
              }}
            >
              {t('wishlist.subtitle')}
            </Typography>
          </Box>

          {/* Action Buttons - Hidden on Mobile */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Stack 
              direction="row" 
              spacing={2} 
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/wish/new')}
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.3)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {t('wishlist.addNewWish')}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Search and Controls */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('wishlist.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            size="small"
          />
          
          {/* View Mode Controls */}
          <Box sx={{ display: 'flex', gap: 1, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
            <IconButton
              onClick={() => handleViewModeChange('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
              size="small"
              sx={{ 
                bgcolor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'text.secondary',
                '&:hover': {
                  bgcolor: viewMode === 'grid' ? 'primary.dark' : 'grey.200'
                }
              }}
            >
              <GridIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => handleViewModeChange('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              size="small"
              sx={{ 
                bgcolor: viewMode === 'list' ? 'primary.main' : 'transparent',
                color: viewMode === 'list' ? 'white' : 'text.secondary',
                '&:hover': {
                  bgcolor: viewMode === 'list' ? 'primary.dark' : 'grey.200'
                }
              }}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('wishlist.showingResults', { count: filteredWishItems.length, total: wishItems.length })}
          </Typography>
          
          {/* Mobile Add Button */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/wish/new')}
              size="small"
              sx={{ 
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              {t('wishlist.addNewWish')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Wish Items */}
      {filteredWishItems.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? t('wishlist.noSearchResults') : t('wishlist.noWishItems')}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/wish/new')}
              sx={{ mt: 2 }}
            >
              {t('wishlist.addFirstWish')}
            </Button>
          )}
        </Paper>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={3}>
              {filteredWishItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Card sx={{ height: '100%', position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={getImageUrl(item.imageUrl, item.name)}
                      alt={item.name}
                      sx={{ objectFit: 'cover' }}
                    />
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleConvert(item)}
                        sx={{ bgcolor: 'primary.main', color: 'white', mr: 1 }}
                        title={t('wishlist.convertToToy')}
                      >
                        <ConvertIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(item)}
                        sx={{ bgcolor: 'white', color: 'primary.main', mr: 1 }}
                        title={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item)}
                        sx={{ bgcolor: 'white', color: 'error.main' }}
                        title={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {item.brand} • {item.code}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        {item.type && <Chip label={item.type} size="small" />}
                        {item.scale && <Chip label={item.scale} size="small" />}
                      </Box>
                      <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                        {userCurrency} {Math.round(item.price || 0).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <List>
              {filteredWishItems.map((item) => (
                <ListItem key={item.id} sx={{ mb: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                  <ListItemAvatar>
                    <Avatar
                      src={getImageUrl(item.imageUrl, item.name)}
                      alt={item.name}
                      sx={{ width: 60, height: 60 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.brand} • {item.code} • {item.type} • {item.scale}
                        </Typography>
                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600, mt: 1 }}>
                          {userCurrency} {Math.round(item.price || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        onClick={() => handleConvert(item)}
                        color="primary"
                        title={t('wishlist.convertToToy')}
                      >
                        <ConvertIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEdit(item)}
                        color="primary"
                        title={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(item)}
                        color="error"
                        title={t('common.delete')}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </>
      )}

      {/* Back to Top Button */}
      {showScrollTop && (
        <Fab
          color="primary"
          aria-label="scroll to top"
          onClick={handleScrollToTop}
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
        >
          <ArrowUpIcon />
        </Fab>
      )}

      {/* Floating Add Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add new wish item"
        onClick={() => navigate('/wish/new')}
        sx={{
          position: 'fixed',
          bottom: showScrollTop ? 80 : 16,
          right: 16,
          zIndex: 1000,
          display: { xs: 'flex', md: 'none' } // Only show on mobile
        }}
      >
        <AddIcon />
      </Fab>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('wishlist.deleteConfirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('wishlist.deleteConfirmDescription')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Confirmation Dialog */}
      <Dialog 
        open={openConvertDialog} 
        onClose={() => setOpenConvertDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {t('wishlist.congratulations')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
              {t('wishlist.finallyGotIt')}
            </Typography>
          </Box>
          
          {itemToConvert && (
            <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {itemToConvert.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {itemToConvert.brand} • {itemToConvert.code}
              </Typography>
            </Box>
          )}
          
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
            {t('wishlist.thatsAwesome')}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t('wishlist.readyToCelebrate')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
          <Button 
            onClick={() => setOpenConvertDialog(false)} 
            disabled={converting}
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            {t('wishlist.maybeLater')}
          </Button>
          <Button 
            onClick={confirmConvert} 
            color="primary" 
            variant="contained"
            disabled={converting}
            startIcon={converting ? <CircularProgress size={16} /> : <ConvertIcon />}
            sx={{ 
              minWidth: 140,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
              }
            }}
          >
            {converting ? t('wishlist.addingToCollection') : t('wishlist.yesAddToCollection')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default WishList; 